// src/pages/ApprovazioneCartelliniPage.js
import GiustificazioniPresenzePanel from "../components/GiustificazioniPresenzePanel";
import React, { useState, useEffect } from "react";
import { esportaXLSX } from "../utils/esportaXLSX";
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp, setDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { CheckSquare, X, RotateCcw, Users } from "lucide-react";
import { useToast } from "../utils/Toast";
import { useCanSee } from "../hooks/useCanSee";
import { contabilizzaApprovazioneCartellino, eliminaMovimentiCartellino } from "../utils/contabilizzaPresenze";
import { minutiLavoroGiornata } from "../utils/segmentiGiornata";

const MESI = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
               "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

export default function ApprovazioneCartelliniPage() {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId: orgId } = useOrg();
  const { addToast } = useToast();

  const [cartellini, setCartellini] = useState([]);
  const [utenti, setUtenti] = useState([]); // tutti gli utenti org da collezione users
  const [approvatori, setApprovatori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStato, setFiltroStato] = useState("inviata");
  const [showGestisciDelega, setShowGestisciDelega] = useState(false);
  const [orgSettings, setOrgSettings] = useState({});

  const canSee = useCanSee();
  const canManage = isOrgAdmin || isPlatformOwner || canSee("approvazione-cartellini");

  // Carica impostazioni org
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(doc(db,"organizations",orgId), snap => {
      if (snap.exists()) setOrgSettings(snap.data());
    });
  }, [orgId]);

  // Carica utenti org dalla collezione users (stessa logica di PresenzePage)
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(
      query(collection(db,"users"), where("organizationId","==",orgId), where("enabled","==",true)),
      snap => setUtenti(snap.docs.map(d => d.data()).sort((a,b)=>(a.displayName||"").localeCompare(b.displayName||"","it")))
    );
  }, [orgId]);

  // Carica approvatori delegati
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(doc(db,"presenzeApprovatori",orgId), snap => {
      setApprovatori(snap.exists() ? (snap.data().uids||[]) : []);
    });
  }, [orgId]);

  // Carica tutti i cartellini inviati/approvati/respinti
  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    const q = query(collection(db,"presenze"),
      where("orgId","==",orgId),
      where("stato","in",["inviata","approvata","respinta"])
    );
    return onSnapshot(q, snap => {
      setCartellini(snap.docs.map(d => ({ id:d.id, ...d.data() })));
      setLoading(false);
    });
  }, [orgId]);

  const nomeUtente = (uid) => {
    const u = utenti.find(u => u.uid === uid);
    return u?.displayName || uid?.slice(0,8)+"…";
  };

  const aggiornaStato = async (cartellino, nuovoStato) => {
    // Stesso principio delle Note Spese: nessuno approva o respinge il proprio cartellino,
    // titolare o admin che sia — eccetto il Platform Owner, per le sue prove.
    const staBypassando = (nuovoStato === "approvata" || nuovoStato === "respinta") && cartellino.uid === currentUser?.uid;
    if (staBypassando && !isPlatformOwner) {
      addToast("Non puoi approvare o respingere un tuo stesso cartellino — serve un altro amministratore","error");
      return;
    }
    try {
      if (nuovoStato === "approvata") {
        const esito = await contabilizzaApprovazioneCartellino({
          orgId: cartellino.orgId,
          uid: cartellino.uid,
          anno: cartellino.anno,
          mese: cartellino.mese,
          giorni: cartellino.giorni || {},
          nomeUtente: nomeUtente(cartellino.uid),
          docId: cartellino.id,
        });
        if (esito && esito.ok === false) {
          addToast(esito.motivo, "error");
          return; // approvazione bloccata: nessuna ora contabilizzata a costo zero
        }
        if (esito?.segmentiScartati?.length > 0) {
          addToast(`Approvato, ma ${esito.segmentiScartati.length} segmento/i non collegati a nessuna commessa valida non sono stati contabilizzati: ${esito.segmentiScartati.slice(0,3).join(" · ")}${esito.segmentiScartati.length>3?"…":""}`, "info");
        }
      }
      if (nuovoStato === "respinta" || nuovoStato === "bozza") {
        await eliminaMovimentiCartellino({ docId: cartellino.id });
      }
      await updateDoc(doc(db,"presenze",cartellino.id), {
        stato: nuovoStato,
        [`${nuovoStato}Da`]: currentUser.uid,
        [`${nuovoStato}At`]: serverTimestamp(),
        ...(staBypassando ? { [`${nuovoStato}ComeBypassPlatformOwner`]: true } : {}),
      });
      const label = staBypassando
        ? `⚠️ ${nuovoStato==="approvata"?"Approvato":"Respinto"} come Platform Owner — un utente normale non potrebbe farlo su un proprio cartellino`
        : nuovoStato==="approvata" ? "Cartellino approvato ✅"
        : nuovoStato==="respinta" ? "Cartellino respinto"
        : "Cartellino rimandato al compilatore";
      addToast(label, staBypassando ? "info" : nuovoStato==="approvata" ? "success" : "info");
    } catch(e) {
      addToast(messaggioErrore(e), "error");
    }
  };

  const toggleApprovatore = async (uid) => {
    const nuovi = approvatori.includes(uid)
      ? approvatori.filter(u => u !== uid)
      : [...approvatori, uid];
    await setDoc(doc(db,"presenzeApprovatori",orgId), { uids: nuovi }, { merge:true });
  };

  const cartelliniFiltered = cartellini
    .filter(c => filtroStato === "tutti" ? true : c.stato === filtroStato)
    .sort((a,b) => (b.updatedAt?.toMillis?.()||0) - (a.updatedAt?.toMillis?.()||0));

  const countPerStato = {
    inviata:   cartellini.filter(c=>c.stato==="inviata").length,
    approvata: cartellini.filter(c=>c.stato==="approvata").length,
    respinta:  cartellini.filter(c=>c.stato==="respinta").length,
  };

  // Utenti delegabili: tutti tranne currentUser e admin/owner
  const utentiDelegabili = utenti.filter(u => u.uid !== currentUser?.uid);

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      {/* Header */}
      <div style={{ background:"linear-gradient(135deg,#1e1b4b,#312e81)", padding:"16px 24px", flexShrink:0 }}>

        {/* Titolo + Gestisci deleghe affiancati */}
        <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:12, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <CheckSquare size={20} color="#c4b5fd"/>
            <h2 style={{ fontSize:18, fontWeight:800, color:"#fff", margin:0 }}>Approvazione Cartellini</h2>
            <button onClick={()=>{
              const MESI=["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
              const intestazione=["Dipendente","Mese","Anno","Stato","Ore Totali","Inviato Il","Approvato Il","Approvato Da"];
              const righe=(cartellini||[]).map(k=>[
                k.nomeUtente||"—", MESI[(k.mese||1)-1]||"—", k.anno||"—", k.stato||"—",
                k.oreTotali?`${k.oreTotali}h`:"—",
                k.inviataIl?.toDate?k.inviataIl.toDate().toLocaleDateString("it-IT"):"—",
                k.approvataIl?.toDate?k.approvataIl.toDate().toLocaleDateString("it-IT"):"—",
                k.approvataDa||"—"
              ]);
              esportaXLSX([intestazione,...righe],`Cartellini_${new Date().toLocaleDateString("it-IT").replace(/\//g,"-")}`,"Cartellini");
            }} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 14px",border:"1px solid #a7f3d0",borderRadius:7,background:"rgba(255,255,255,.15)",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              📊 Export Excel
            </button>
          </div>
          {canManage && (
            <button onClick={() => setShowGestisciDelega(s=>!s)}
              style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 14px",
                border:"1px solid rgba(255,255,255,0.3)", borderRadius:8,
                background: showGestisciDelega ? "rgba(124,58,237,0.4)" : "rgba(255,255,255,0.1)",
                color:"#e9d5ff", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              <Users size={13}/> Gestisci deleghe
            </button>
          )}
        </div>

        {/* Pannello deleghe */}
        {showGestisciDelega && canManage && (
          <div style={{ marginBottom:12, background:"rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 16px" }}>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.7)", fontWeight:600, marginBottom:8 }}>
              Seleziona chi può approvare i cartellini oltre a Owner e Admin:
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
              {utentiDelegabili.length === 0 && (
                <span style={{ fontSize:12, color:"rgba(255,255,255,0.4)" }}>
                  Nessun altro utente attivo nell'organizzazione
                </span>
              )}
              {utentiDelegabili.map(u => {
                const isDelega = approvatori.includes(u.uid);
                return (
                  <button key={u.uid} onClick={() => toggleApprovatore(u.uid)}
                    style={{ padding:"5px 14px", borderRadius:20,
                      border:`1px solid ${isDelega?"#a78bfa":"rgba(255,255,255,0.2)"}`,
                      background: isDelega?"rgba(124,58,237,0.5)":"rgba(255,255,255,0.05)",
                      color: isDelega?"#fff":"rgba(255,255,255,0.6)",
                      fontSize:12, fontWeight: isDelega?700:400, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                    {isDelega ? "✓ " : ""}{u.displayName || u.email}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Filtri stato */}
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {[
            { id:"inviata",   label:`In attesa (${countPerStato.inviata})`,   bg:"#dbeafe", col:"#1d4ed8" },
            { id:"approvata", label:`Approvati (${countPerStato.approvata})`, bg:"#d1fae5", col:"#065f46" },
            { id:"respinta",  label:`Respinti (${countPerStato.respinta})`,   bg:"#fee2e2", col:"#991b1b" },
            { id:"tutti",     label:"Tutti",                                   bg:"#f3f4f6", col:"#374151" },
          ].map(f => (
            <button key={f.id} onClick={() => setFiltroStato(f.id)}
              style={{ padding:"5px 12px", borderRadius:20, border:"none",
                background: filtroStato===f.id ? f.bg : "rgba(255,255,255,0.1)",
                color: filtroStato===f.id ? f.col : "rgba(255,255,255,0.6)",
                fontSize:11, fontWeight: filtroStato===f.id?700:400, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Lista cartellini */}
      <div style={{ flex:1, overflowY:"auto", padding:24, background:"#f9fafb" }}>
        <GiustificazioniPresenzePanel orgId={orgId} approvatori={approvatori}/>
        {loading && <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>⏳ Caricamento...</div>}

        {!loading && cartelliniFiltered.length === 0 && (
          <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>
            <CheckSquare size={40} color="#d1d5db" style={{ marginBottom:12 }}/>
            <div style={{ fontSize:14 }}>
              {filtroStato==="inviata" ? "Nessun cartellino in attesa di approvazione"
               : filtroStato==="approvata" ? "Nessun cartellino approvato"
               : filtroStato==="respinta" ? "Nessun cartellino respinto"
               : "Nessun cartellino"}
            </div>
          </div>
        )}

        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {cartelliniFiltered.map(c => {
            const nome = nomeUtente(c.uid);
            const meseLabel = `${MESI[(c.mese||1)-1]} ${c.anno}`;
            const statoInfo = {
              inviata:   { label:"In attesa", bg:"#dbeafe", col:"#1d4ed8" },
              approvata: { label:"Approvato",  bg:"#d1fae5", col:"#065f46" },
              respinta:  { label:"Respinto",   bg:"#fee2e2", col:"#991b1b" },
            }[c.stato] || { label:c.stato, bg:"#f3f4f6", col:"#374151" };

            // Calcolo ore totali dal cartellino
            const totMinuti = Object.values(c.giorni||{}).reduce((tot, g) => {
              // ore di lavoro dal calcolo unico del cartellino (permessi a ore già tolti)
              const min = minutiLavoroGiornata(g.timbrature||[]);
              return tot + Math.max(0, min-(min>=360?60:0));
            }, 0);

            return (
              <div key={c.id} style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e7eb",
                padding:"14px 18px", display:"flex", alignItems:"center",
                justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>

                {/* Info operaio */}
                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:40, height:40, borderRadius:"50%", background:"#ede9fe",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:16, fontWeight:800, color:"#7c3aed", flexShrink:0 }}>
                    {nome[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:"#111827" }}>{nome}</div>
                    <div style={{ fontSize:12, color:"#6b7280" }}>{meseLabel}</div>
                    <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>
                      Ore totali: <strong style={{ color:"#059669" }}>{(totMinuti/60).toFixed(1)}h</strong>
                    </div>
                  </div>
                </div>

                {/* Stato + tasti */}
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  <div>
                    <span style={{ fontSize:11, fontWeight:700, color:statoInfo.col,
                      background:statoInfo.bg, borderRadius:20, padding:"3px 10px" }}>
                      {statoInfo.label}
                    </span>
                    {(c.stato==="approvata"||c.stato==="respinta") && c[`${c.stato}Da`] && (
                      <div style={{ fontSize:10, color:"#9ca3af", marginTop:4 }}>
                        {c.stato==="approvata"?"✅ Approvata":"❌ Respinta"} da <strong style={{color:"#374151"}}>{nomeUtente(c[`${c.stato}Da`])}</strong>
                        {c[`${c.stato}At`]?.toDate && (
                          <span> il {c[`${c.stato}At`].toDate().toLocaleDateString("it-IT")}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {c.stato === "inviata" && c.uid === currentUser?.uid && !isPlatformOwner && (
                    <div style={{fontSize:11.5,color:"#9ca3af",fontStyle:"italic",padding:"6px 4px"}}>
                      È il tuo cartellino — serve un altro amministratore per approvarlo
                    </div>
                  )}
                  {c.stato === "inviata" && (c.uid !== currentUser?.uid || isPlatformOwner) && (
                    <>
                      {c.uid === currentUser?.uid && isPlatformOwner && (
                        <span style={{fontSize:10,color:"#7c3aed",fontWeight:700,marginRight:4}} title="È il tuo cartellino — puoi agire solo perché sei Platform Owner">⚠️ tuo cartellino</span>
                      )}
                      <button onClick={() => aggiornaStato(c, "approvata")}
                        style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 14px",
                          border:"1px solid #059669", borderRadius:7, background:"#d1fae5",
                          color:"#059669", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                        <CheckSquare size={13}/> Approva
                      </button>
                      <button onClick={() => aggiornaStato(c, "respinta")}
                        style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 14px",
                          border:"1px solid #dc2626", borderRadius:7, background:"#fee2e2",
                          color:"#dc2626", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                        <X size={13}/> Respingi
                      </button>
                    </>
                  )}

                  {c.stato === "respinta" && (
                    <button onClick={() => aggiornaStato(c, "bozza")}
                      style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 14px",
                        border:"1px solid #2563eb", borderRadius:7, background:"#dbeafe",
                        color:"#2563eb", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                      <RotateCcw size={13}/> Rimanda al compilatore
                    </button>
                  )}

                  {c.stato === "approvata" && canManage && (
                    <button onClick={() => aggiornaStato(c, "bozza")}
                      style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 14px",
                        border:"1px solid #6b7280", borderRadius:7, background:"#f3f4f6",
                        color:"#374151", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                      <RotateCcw size={13}/> Riapri
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
